'use client';

import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { useWatchlistPanel } from '@/context/WatchlistPanelContext';
import { useRightDock } from '@/context/RightDockContext';
import GlobalSidePanel from '@/components/panel/GlobalSidePanel';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import AdBannerWrapper from '@/components/layout/AdBannerWrapper';
import EmailVerificationBanner from '@/components/layout/EmailVerificationBanner';
import FooterAdWrapper from '@/components/layout/FooterAdWrapper';
import TickerTape from '@/components/layout/TickerTape';
import ContentPreviewOverlay from '@/components/layout/ContentPreviewOverlay';
import ScrollToTop from '@/components/layout/ScrollToTop';
import PageGuard from '@/components/layout/PageGuard';
import AnnouncementBanner from '@/components/layout/AnnouncementBanner';
import AnnouncementPopup from '@/components/layout/AnnouncementPopup';
import TrialWelcomePopup from '@/components/layout/TrialWelcomePopup';
import TrialExpiryBanner from '@/components/layout/TrialExpiryBanner';
import ContactFormModal from '@/components/ui/ContactFormModal';
import PreTradingChecklistModal from '@/components/trading/PreTradingChecklistModal';
import AiAssistantWidget from '@/components/assistant/AiAssistantWidget';

const AUTH_PATHS = ['/login', '/create-account', '/verify-email', '/forgot-password', '/reset-password'];

export default function LayoutShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isAuthPage = AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
  const { isOpen: watchlistOpen, width: panelWidth } = useWatchlistPanel();
  // AI Step Log dock width (0 when closed) — published by the chart's panel.
  const { stepLogWidth } = useRightDock();
  // On stock/ETF detail pages the side panels dock — push content left to make
  // room. Everywhere else (and on mobile) they float over, so no shift.
  const isDetailPage = pathname.startsWith('/stocks/') || pathname.startsWith('/etf/');
  const shiftForPanel = watchlistOpen && isDetailPage;
  const shiftForStepLog = stepLogWidth > 0 && isDetailPage;
  // Total right padding so BOTH the watchlist rail and the step-log panel
  // (if open together) shift content enough to clear them.
  const dockPad = (shiftForPanel ? panelWidth : 0) + (shiftForStepLog ? stepLogWidth : 0);
  const anyDock = shiftForPanel || shiftForStepLog;

  // Force scroll to top on every route change. Next.js App Router does
  // this by default but autoFocus on inputs (Navbar search), browser
  // scroll restoration, and async layout shifts could leave pages like
  // /stocks/[ticker] and /heatmap landing partway down. Skip when the URL
  // carries a hash so #anchor navigation still works.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [pathname]);

  // The Pro Terminal is a full-bleed trading workstation: it owns the entire
  // viewport with its OWN top bar + collapsible rails, so we skip the global
  // navbar / banners / footer chrome. Without this the global navbar + ticker
  // tape pushed the terminal's control bar (symbol search, timeframe/range,
  // chart-type, rail toggles) off-screen behind a scroll.
  if (pathname === '/terminal' || pathname.startsWith('/terminal/')) {
    return (
      <main className="h-screen overflow-hidden">
        {children}
        <PreTradingChecklistModal />
        <AiAssistantWidget />
      </main>
    );
  }

  // Auth pages render their own chrome. The "return to home page" affordance
  // used to float above the card here, which cost a row of vertical space on
  // every auth screen; it now lives in the card's top-left corner
  // (AuthCardHeader / AuthHomeButton) so the card is self-contained.
  if (isAuthPage) {
    return (
      <>
        <main className="min-h-screen">{children}</main>
        <AiAssistantWidget />
        <ContactFormModal />
      </>
    );
  }

  return (
    <>
      <EmailVerificationBanner />
      <Navbar />
      <div className="h-14" />
      <div
        className={cn('transition-[padding] duration-200', anyDock && 'md:pr-[length:var(--dock-w)]')}
        style={anyDock ? ({ '--dock-w': `${dockPad}px` } as React.CSSProperties) : undefined}
      >
        <AnnouncementBanner />
        <TickerTape />
        <AdBannerWrapper />
        <ContentPreviewOverlay />
        <AnnouncementPopup />
        <TrialWelcomePopup />
        <PreTradingChecklistModal />
        <TrialExpiryBanner />
        <main className="min-h-[calc(100vh-4rem)]"><PageGuard>{children}</PageGuard></main>
        <ScrollToTop />
        <ContactFormModal />
        <AiAssistantWidget />
        <div className="mt-16">
          <FooterAdWrapper />
          <Footer />
        </div>
      </div>
      <GlobalSidePanel />
    </>
  );
}
